import {
  Search,
  Globe,
  ShieldCheck,
  MessageCircle,
  BarChart3,
  TrendingUp,
  Users,
  Building2,
  GraduationCap,
  HeartHandshake,
  Boxes,
  Sparkles,
  Activity,
  BriefcaseBusiness,
  Database,
  Code2,
  Zap,
  ShoppingCart,
  Layers,
  RefreshCw,
  Workflow,
  Laptop,
  Package,
  Network,
  LayoutDashboard,
  Braces,
  ListTodo,
  Handshake,
  Factory,
  Landmark,
  GitBranch,
  Settings2,
  Contact,
  UserRoundSearch,
  Wallet,
  Calculator,
  Trophy,
  Truck,
} from "lucide-react";

import type { ServicePage } from "@/types/service";

export const SoftwareDevelopments: ServicePage[] = [
  // CRM Software Development
  {
    title: "CRM Software Development",
    slug: "crm-software-development",
    group: "Software Development",
    icon: UserRoundSearch,

    seo: {
      title: "CRM Software Development Services",
      description:
        "Build custom CRM software that centralizes leads, sales pipelines, customer interactions, automation, reporting, and team workflows around your business processes.",
      keywords: [
        "CRM Software Development",
        "Custom CRM Development",
        "CRM Development Services",
        "Customer Relationship Management Software",
        "CRM Software Developers",
        "CRM Software Development Company",
      ],
    },

    hero: {
      title: "CRM Software Development Built Around Your Customer Lifecycle",
      description:
        "Engineer a CRM around your actual pipeline, customer lifecycle, and operating model.",
      highlights: [
        "Lead & Deal Pipelines",
        "Role-Based Dashboards",
        "Automation & Reminders",
        "Customer Visibility",
      ],
    },

    whyChoose: {
      title: "Why Invest in Custom CRM Software?",
      description:
        "Custom CRM software centralizes customer information, sales workflows, team activities, automation, and reporting around the way your organization actually manages relationships and revenue.",
      items: [
        {
          title: "Centralized Customer Data",
          description:
            "Keep leads, customers, conversations, activities, deals, and relevant business information within one structured system.",
          icon: Database,
        },
        {
          title: "Structured Sales Pipelines",
          description:
            "Manage leads and opportunities through clearly defined stages aligned with your actual sales process.",
          icon: GitBranch,
        },
        {
          title: "Workflow Automation",
          description:
            "Automate suitable reminders, assignments, follow-ups, status changes, and recurring CRM activities.",
          icon: Workflow,
        },
        {
          title: "Real-Time Business Visibility",
          description:
            "Give teams and leadership clearer visibility into pipelines, activities, customers, and relevant performance information.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our CRM Software Development Services",
      description:
        "End-to-end CRM development covering customer management, sales pipelines, automation, dashboards, reporting, integrations, and business-specific workflows.",
      items: [
        {
          title: "Custom CRM Development",
          description:
            "Build CRM platforms around your customer lifecycle, sales processes, teams, permissions, and operational requirements.",
          icon: UserRoundSearch,
          features: [
            "Customer Management",
            "Lead Management",
            "Deal Pipelines",
            "Role-Based Access",
            "Custom Workflows",
          ],
        },
        {
          title: "Sales CRM Development",
          description:
            "Create structured sales systems for managing leads, opportunities, activities, follow-ups, deals, and team performance.",
          icon: TrendingUp,
          features: [
            "Lead Pipelines",
            "Deal Tracking",
            "Sales Activities",
            "Follow-Up Management",
            "Sales Reporting",
          ],
        },
        {
          title: "CRM Automation Development",
          description:
            "Automate suitable repetitive customer and sales processes through rules, reminders, assignments, and workflow actions.",
          icon: Workflow,
          features: [
            "Automated Assignments",
            "Follow-Up Reminders",
            "Status Automation",
            "Notifications",
            "Workflow Rules",
          ],
        },
        {
          title: "CRM Integration Services",
          description:
            "Connect CRM software with suitable websites, applications, communication platforms, APIs, and other business systems.",
          icon: Boxes,
          features: [
            "REST API Integration",
            "Website Integration",
            "Communication Integration",
            "Third-Party APIs",
            "Business System Integration",
          ],
        },
      ],
    },

    features: {
      title: "Key CRM Software Features",
      description:
        "Core CRM capabilities for managing customer relationships, sales activities, team workflows, automation, and business visibility.",
      items: [
        {
          title: "Lead Management",
          description:
            "Capture, organize, assign, qualify, and track leads throughout your sales process.",
          icon: UserRoundSearch,
        },
        {
          title: "Deal Pipeline Management",
          description:
            "Track opportunities through configurable pipeline stages with relevant values, activities, ownership, and statuses.",
          icon: GitBranch,
        },
        {
          title: "Customer Profiles",
          description:
            "Maintain centralized customer records containing relevant contact information, activities, deals, notes, and interactions.",
          icon: Contact,
        },
        {
          title: "Task & Follow-Up Management",
          description:
            "Schedule activities, reminders, calls, meetings, and follow-ups associated with leads and customers.",
          icon: ListTodo,
        },
        {
          title: "Role-Based Dashboards",
          description:
            "Provide relevant CRM information and functionality according to user roles, teams, responsibilities, and permissions.",
          icon: LayoutDashboard,
        },
        {
          title: "Reports & Analytics",
          description:
            "Track relevant pipeline activity, sales performance, lead movement, team activities, and customer information.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our CRM Software Development Process",
      description:
        "A structured CRM development process focused on understanding your customer lifecycle, mapping sales workflows, designing suitable modules, and building a system around actual operations.",
      items: [
        {
          title: "CRM Requirement Discovery",
          description:
            "Understand your sales process, customer lifecycle, teams, lead sources, workflows, reporting requirements, and existing systems.",
        },
        {
          title: "Process & Architecture Planning",
          description:
            "Map pipelines, customer stages, CRM modules, user roles, permissions, automations, integrations, and database architecture.",
        },
        {
          title: "CRM UI/UX Design",
          description:
            "Design dashboards, customer profiles, pipeline views, forms, activities, reports, and workflows around day-to-day CRM usage.",
        },
        {
          title: "CRM Development",
          description:
            "Develop customer management, pipelines, dashboards, automation, reporting, APIs, authentication, and required integrations.",
        },
        {
          title: "Testing & Workflow Validation",
          description:
            "Test CRM workflows, permissions, pipelines, automation, integrations, reporting, data handling, and user experiences.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the CRM and provide ongoing support, workflow improvements, integrations, optimization, and additional functionality.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom CRM Software Development",
      description:
        "Create a centralized customer platform that improves sales organization, team visibility, workflow consistency, and control over customer relationships.",
      items: [
        {
          title: "Centralized Customer Information",
          description:
            "Give authorized teams access to relevant customer, lead, deal, activity, and interaction information from one system.",
          icon: Database,
        },
        {
          title: "Better Sales Organization",
          description:
            "Structure leads, opportunities, activities, and follow-ups around defined sales pipelines and processes.",
          icon: GitBranch,
        },
        {
          title: "Reduced Manual Work",
          description:
            "Automate suitable repetitive CRM activities such as reminders, assignments, notifications, and workflow updates.",
          icon: Zap,
        },
        {
          title: "Improved Team Visibility",
          description:
            "Give managers and teams clearer visibility into ownership, customer activities, pipelines, and relevant sales information.",
          icon: Users,
        },
        {
          title: "Business-Specific Workflows",
          description:
            "Configure CRM modules, stages, permissions, fields, and processes around the way your organization operates.",
          icon: Settings2,
        },
        {
          title: "Scalable CRM Operations",
          description:
            "Extend users, pipelines, modules, automation, integrations, and reporting as your customer operations grow.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "CRM Software Development Technologies",
      description:
        "Modern technologies used to build scalable CRM interfaces, backend services, relational data models, and business integrations.",
      technologies: ["React", "Node.js", "PostgreSQL", "REST"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We build CRM platforms around actual customer journeys and operational workflows instead of forcing businesses into predefined generic processes.",
      items: [
        {
          title: "Business-First CRM Development",
          description:
            "Start with your sales process, customer lifecycle, teams, and operating model before defining CRM functionality.",
          icon: BriefcaseBusiness,
        },
        {
          title: "Custom Pipeline Architecture",
          description:
            "Design lead and deal pipelines around your actual stages, ownership models, activities, and sales workflows.",
          icon: GitBranch,
        },
        {
          title: "Role-Based CRM Experiences",
          description:
            "Create suitable dashboards, permissions, information, and functionality for different users and teams.",
          icon: Users,
        },
        {
          title: "Automation & Integration",
          description:
            "Connect CRM workflows with suitable APIs, communication services, websites, applications, and business systems.",
          icon: Workflow,
        },
        {
          title: "Scalable Software Architecture",
          description:
            "Build maintainable CRM foundations capable of supporting additional modules, users, workflows, and integrations.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your CRM with troubleshooting, workflow improvements, integrations, optimization, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "CRM Solutions for Different Industries",
      description:
        "Custom CRM platforms tailored to different customer lifecycles, sales processes, service models, and operational requirements.",
      items: [
        {
          title: "Real Estate",
          description:
            "Manage property enquiries, leads, agents, follow-ups, customer requirements, site visits, and deal pipelines.",
          icon: Building2,
        },
        {
          title: "Professional Services",
          description:
            "Track prospects, client relationships, proposals, activities, follow-ups, and service opportunities.",
          icon: BriefcaseBusiness,
        },
        {
          title: "Education",
          description:
            "Manage enquiries, prospective students, admissions follow-ups, communication, counsellor activities, and enrollment pipelines.",
          icon: GraduationCap,
        },
        {
          title: "Healthcare & Services",
          description:
            "Create appropriate customer relationship workflows for enquiries, service interactions, communication, and follow-ups.",
          icon: HeartHandshake,
        },
        {
          title: "B2B Sales",
          description:
            "Manage companies, contacts, opportunities, sales activities, account ownership, pipelines, and long-term relationships.",
          icon: Handshake,
        },
        {
          title: "Technology & SaaS",
          description:
            "Track product enquiries, demos, opportunities, customer interactions, sales pipelines, and account activity.",
          icon: Laptop,
        },
      ],
    },

    portfolio: {
      title: "CRM Software Development Success Stories",
      description:
        "Custom CRM platforms focused on centralized customer information, structured sales workflows, automation, dashboards, and reporting.",
      items: [
        {
          title: "Sales Pipeline CRM",
          description:
            "Developed a custom CRM with lead capture, configurable deal stages, sales activities, automated reminders, role-based dashboards, and performance reporting.",
          image: "/project-image.webp",
        },
        {
          title: "Real Estate CRM Platform",
          description:
            "Built a customer management system connecting property enquiries, lead assignments, agent activities, follow-ups, site visits, and deal pipelines.",
          image: "/project-image.webp",
        },
        {
          title: "B2B Customer Management System",
          description:
            "Created a centralized CRM for managing companies, contacts, opportunities, sales activities, account ownership, reporting, and customer history.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire CRM Software Developers",
      description:
        "Partner with our CRM developers to design, build, integrate, optimize, and support customer management software around your actual sales and service workflows.",
      cta: "Let's Build Your CRM Software",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to turn fragmented sales and customer processes into structured CRM platforms.",
      items: [
        {
          name: "James Walker",
          company: "NorthPoint Sales",
          location: "United Kingdom",
          review:
            "Our previous process was spread across spreadsheets and several different tools. Skand Tech Hub built the CRM around our actual pipeline, which has made lead ownership and follow-ups much easier for the team.",
        },
        {
          name: "Michael Roberts",
          company: "CoreAxis Solutions",
          location: "United States",
          review:
            "The custom dashboards and workflow automation have given our managers much better visibility into sales activity. The CRM feels designed around how our business actually operates.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our CRM Software Development services.",
      items: [
        {
          question: "What is custom CRM software development?",
          answer:
            "Custom CRM development involves building customer relationship management software around your specific sales processes, customer lifecycle, teams, pipelines, permissions, automation, reporting, and integration requirements.",
        },
        {
          question: "Can you build a CRM around our existing sales process?",
          answer:
            "Yes. We can map your existing lead and sales workflows and build suitable pipelines, stages, activities, roles, permissions, dashboards, and automation around them.",
        },
        {
          question:
            "Can the CRM have different dashboards for different users?",
          answer:
            "Yes. CRM software can include role-based dashboards, permissions, views, and functionality for sales representatives, managers, administrators, service teams, and other suitable users.",
        },
        {
          question: "Can you automate follow-ups and reminders?",
          answer:
            "Yes. Suitable CRM workflows can automate reminders, notifications, assignments, status changes, follow-up activities, and other repetitive processes.",
        },
        {
          question: "Can the CRM integrate with other business systems?",
          answer:
            "Yes. Where suitable APIs or integration methods are available, the CRM can connect with websites, applications, communication platforms, external APIs, and other business systems.",
        },
        {
          question: "Do you provide ongoing CRM development and support?",
          answer:
            "Yes. We can provide troubleshooting, workflow changes, new modules, integrations, reporting improvements, optimization, and additional CRM development.",
        },
      ],
    },
  },
  // ERP Software Development
  {
    title: "ERP Software Development",
    slug: "erp-software-development",
    group: "Software Development",
    icon: Settings2,

    seo: {
      title: "ERP Software Development Services",
      description:
        "Build custom ERP software that connects finance, inventory, HR, sales, procurement, reporting, and business operations within one centralized platform.",
      keywords: [
        "ERP Software Development",
        "Custom ERP Development",
        "ERP Development Services",
        "Enterprise Resource Planning Software",
        "ERP Software Developers",
        "ERP Software Development Company",
      ],
    },

    hero: {
      title: "ERP Software Development for Connected Business Operations",
      description:
        "Replace fragmented tools with connected ERP modules that give leadership real-time visibility.",
      highlights: [
        "Business Process Mapping",
        "Inventory & Finance Modules",
        "Executive Reporting",
        "Connected Operations",
      ],
    },

    whyChoose: {
      title: "Why Invest in Custom ERP Software?",
      description:
        "Custom ERP software connects critical business processes, departments, data, and reporting within one structured system designed around how your organization actually operates.",
      items: [
        {
          title: "Centralized Business Operations",
          description:
            "Connect finance, inventory, sales, procurement, HR, and other operational workflows within one business platform.",
          icon: Boxes,
        },
        {
          title: "Real-Time Business Visibility",
          description:
            "Give leadership and authorized teams access to relevant operational data, reports, and business information.",
          icon: BarChart3,
        },
        {
          title: "Process Automation",
          description:
            "Automate suitable approvals, calculations, notifications, assignments, and repetitive operational workflows.",
          icon: Workflow,
        },
        {
          title: "Scalable Business Management",
          description:
            "Extend ERP modules, users, workflows, locations, integrations, and reporting as organizational requirements grow.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our ERP Software Development Services",
      description:
        "End-to-end ERP development covering business process mapping, finance, inventory, HR, sales, procurement, dashboards, reporting, integrations, and custom operational modules.",
      items: [
        {
          title: "Custom ERP Development",
          description:
            "Build ERP platforms around your departments, operational workflows, users, approvals, business rules, and reporting requirements.",
          icon: Settings2,
          features: [
            "Custom ERP Modules",
            "Business Workflows",
            "Role-Based Access",
            "Approval Processes",
            "Centralized Data",
          ],
        },
        {
          title: "Finance & Accounting Modules",
          description:
            "Develop suitable financial workflows for transactions, expenses, invoices, payments, accounting records, and management reporting.",
          icon: Landmark,
          features: [
            "Invoice Management",
            "Expense Management",
            "Payment Records",
            "Financial Workflows",
            "Financial Reporting",
          ],
        },
        {
          title: "Inventory & Procurement Modules",
          description:
            "Manage products, stock, suppliers, purchases, inventory movement, procurement workflows, and operational records.",
          icon: Package,
          features: [
            "Inventory Management",
            "Stock Tracking",
            "Supplier Management",
            "Purchase Management",
            "Procurement Workflows",
          ],
        },
        {
          title: "ERP Integration & Modernization",
          description:
            "Connect ERP modules with suitable applications, APIs, reporting platforms, and existing business systems while modernizing fragmented workflows.",
          icon: RefreshCw,
          features: [
            "API Integration",
            "System Integration",
            "Data Migration",
            "Workflow Modernization",
            "Reporting Integration",
          ],
        },
      ],
    },

    features: {
      title: "Key ERP Software Features",
      description:
        "Core ERP capabilities for managing connected business operations, users, workflows, data, approvals, and organizational reporting.",
      items: [
        {
          title: "Finance Management",
          description:
            "Manage suitable financial records, transactions, invoices, expenses, payments, and reporting through structured workflows.",
          icon: Landmark,
        },
        {
          title: "Inventory Management",
          description:
            "Track products, stock levels, inventory movements, locations, availability, and related operational information.",
          icon: Package,
        },
        {
          title: "HR & Employee Management",
          description:
            "Centralize suitable employee information, organizational structures, attendance, leave, and HR-related workflows.",
          icon: Users,
        },
        {
          title: "Sales Management",
          description:
            "Manage customers, quotations, orders, sales activities, transactions, and related operational processes.",
          icon: TrendingUp,
        },
        {
          title: "Procurement Management",
          description:
            "Structure supplier management, purchase requests, approvals, purchase orders, and procurement workflows.",
          icon: ShoppingCart,
        },
        {
          title: "Executive Dashboards",
          description:
            "Provide leadership with relevant operational metrics, reports, trends, and cross-department business visibility.",
          icon: LayoutDashboard,
        },
      ],
    },

    process: {
      title: "Our ERP Software Development Process",
      description:
        "A structured ERP development process focused on understanding business operations, mapping workflows, defining connected modules, and building software around organizational requirements.",
      items: [
        {
          title: "Business Process Discovery",
          description:
            "Understand departments, operational workflows, existing tools, users, approvals, data, reporting requirements, and business challenges.",
        },
        {
          title: "Process Mapping & ERP Architecture",
          description:
            "Map business processes and define ERP modules, relationships, permissions, workflows, integrations, database architecture, and reporting requirements.",
        },
        {
          title: "ERP UI/UX Design",
          description:
            "Design dashboards, forms, management screens, workflows, reports, and role-specific experiences for day-to-day ERP operations.",
        },
        {
          title: "ERP Development",
          description:
            "Develop ERP modules, business logic, approvals, database workflows, APIs, dashboards, reporting, and required integrations.",
        },
        {
          title: "Testing & Process Validation",
          description:
            "Test cross-module workflows, calculations, permissions, approvals, integrations, reporting, data handling, and operational scenarios.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the ERP platform and provide ongoing support, module enhancements, workflow improvements, integrations, and optimization.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom ERP Software Development",
      description:
        "Connect departments, reduce fragmented processes, centralize business data, and give leadership better visibility into organizational operations.",
      items: [
        {
          title: "Unified Business Platform",
          description:
            "Bring suitable finance, inventory, HR, sales, procurement, and operational processes into one connected system.",
          icon: Boxes,
        },
        {
          title: "Reduced Manual Processes",
          description:
            "Automate suitable repetitive tasks, calculations, approvals, notifications, and information movement between workflows.",
          icon: Zap,
        },
        {
          title: "Improved Data Visibility",
          description:
            "Give authorized users access to relevant operational information without relying on disconnected spreadsheets and systems.",
          icon: Database,
        },
        {
          title: "Better Process Control",
          description:
            "Standardize business workflows, approvals, responsibilities, statuses, and operational rules across departments.",
          icon: Workflow,
        },
        {
          title: "Executive Decision Support",
          description:
            "Provide leadership with dashboards and reports based on relevant information generated across connected ERP modules.",
          icon: BarChart3,
        },
        {
          title: "Scalable Operations",
          description:
            "Extend ERP functionality with additional users, departments, modules, locations, integrations, and workflows.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "ERP Software Development Technologies",
      description:
        "Technologies used to build ERP interfaces, backend business logic, relational data systems, and management reporting.",
      technologies: ["React", "Laravel", "PostgreSQL", "Power BI"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We approach ERP development from business processes first, translating operational workflows into connected software modules rather than forcing organizations into generic systems.",
      items: [
        {
          title: "Business Process Mapping",
          description:
            "Understand existing workflows, departments, responsibilities, approvals, and operational dependencies before development begins.",
          icon: Workflow,
        },
        {
          title: "Custom ERP Modules",
          description:
            "Build modules around your actual finance, inventory, HR, sales, procurement, and operational requirements.",
          icon: Settings2,
        },
        {
          title: "Connected Architecture",
          description:
            "Design ERP modules to share relevant information and support cross-department business workflows.",
          icon: Network,
        },
        {
          title: "Role-Based Operations",
          description:
            "Provide suitable dashboards, permissions, workflows, and information according to organizational roles and responsibilities.",
          icon: Users,
        },
        {
          title: "Reporting & Business Visibility",
          description:
            "Create operational dashboards and reports that give management clearer visibility across connected ERP modules.",
          icon: BarChart3,
        },
        {
          title: "Ongoing ERP Support",
          description:
            "Support your ERP with new modules, workflow changes, integrations, troubleshooting, optimization, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "ERP Solutions for Different Industries",
      description:
        "Custom ERP systems tailored to different operational processes, departmental structures, inventory requirements, financial workflows, and reporting needs.",
      items: [
        {
          title: "Manufacturing",
          description:
            "Connect inventory, procurement, production-related workflows, suppliers, finance, reporting, and operational management.",
          icon: Factory,
        },
        {
          title: "Retail & Distribution",
          description:
            "Manage products, inventory, purchasing, suppliers, sales, locations, transactions, and operational reporting.",
          icon: ShoppingCart,
        },
        {
          title: "Professional Services",
          description:
            "Centralize clients, projects, employees, expenses, billing, operational workflows, and management reporting.",
          icon: BriefcaseBusiness,
        },
        {
          title: "Education",
          description:
            "Connect suitable administrative, finance, HR, procurement, inventory, and institutional management workflows.",
          icon: GraduationCap,
        },
        {
          title: "Healthcare",
          description:
            "Build appropriate operational systems connecting administration, inventory, procurement, finance, employees, and reporting.",
          icon: HeartHandshake,
        },
        {
          title: "Multi-Location Businesses",
          description:
            "Centralize relevant operational information while supporting locations, departments, users, inventory, and management reporting.",
          icon: Building2,
        },
      ],
    },

    portfolio: {
      title: "ERP Software Development Success Stories",
      description:
        "Custom ERP platforms focused on connected business operations, centralized data, workflow automation, and management visibility.",
      items: [
        {
          title: "Integrated Business ERP",
          description:
            "Developed a modular ERP connecting inventory, procurement, sales, finance, users, approvals, and executive reporting within one centralized platform.",
          image: "/project-image.webp",
        },
        {
          title: "Inventory & Procurement ERP",
          description:
            "Built an operational system for stock management, suppliers, purchase requests, approvals, purchase orders, inventory movement, and management reporting.",
          image: "/project-image.webp",
        },
        {
          title: "Multi-Department Management Platform",
          description:
            "Created a centralized ERP with role-based modules, departmental workflows, shared business data, dashboards, approvals, and reporting.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire ERP Software Developers",
      description:
        "Partner with our ERP developers to map your business processes and build connected software modules around finance, inventory, HR, sales, procurement, and operations.",
      cta: "Let's Build Your ERP Software",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to transform fragmented operational processes into connected ERP platforms.",
      items: [
        {
          name: "Robert Harrison",
          company: "Northfield Operations",
          location: "United Kingdom",
          review:
            "Our teams were working across spreadsheets and disconnected systems. The ERP brought our inventory, purchasing, finance, and reporting into a much more structured workflow.",
        },
        {
          name: "David Anderson",
          company: "CoreLine Industries",
          location: "United States",
          review:
            "Skand Tech Hub took the time to understand how our departments actually work before building the system. The dashboards and connected workflows now give management much better visibility.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our ERP Software Development services.",
      items: [
        {
          question: "What is custom ERP software development?",
          answer:
            "Custom ERP development involves building an enterprise resource planning system around your organization's specific departments, workflows, users, approvals, data, integrations, and reporting requirements.",
        },
        {
          question: "Which modules can be included in custom ERP software?",
          answer:
            "Depending on your requirements, an ERP can include finance, inventory, procurement, sales, HR, employee management, reporting, dashboards, approvals, administration, and other custom operational modules.",
        },
        {
          question: "Can you build ERP software around our existing processes?",
          answer:
            "Yes. We can map your existing business processes and translate suitable workflows, roles, approvals, data relationships, and operational rules into custom ERP modules.",
        },
        {
          question: "Can different departments have different ERP access?",
          answer:
            "Yes. ERP software can include role-based permissions, dashboards, modules, workflows, and data access according to departments and user responsibilities.",
        },
        {
          question: "Can ERP software integrate with existing systems?",
          answer:
            "Yes. Where suitable APIs or integration methods are available, ERP software can connect with existing applications, external services, reporting platforms, and other business systems.",
        },
        {
          question: "Do you provide ongoing ERP development and support?",
          answer:
            "Yes. We can provide troubleshooting, new modules, workflow changes, integrations, reporting improvements, optimization, and additional ERP development.",
        },
      ],
    },
  },
  // MLM Software Development
  {
    title: "MLM Software Development",
    slug: "mlm-software-development",
    group: "Software Development",
    icon: Activity,

    seo: {
      title: "MLM Software Development Services",
      description:
        "Build custom MLM software for distributor networks, commission calculations, wallets, payouts, reporting, and network management with transparent and scalable workflows.",
      keywords: [
        "MLM Software Development",
        "MLM Software Development Services",
        "Custom MLM Software",
        "Multi Level Marketing Software",
        "MLM Software Developers",
        "MLM Software Development Company",
      ],
    },

    hero: {
      title: "MLM Software Development for Complex Network Operations",
      description:
        "Run complex network operations with transparent calculations, dashboards, and compliance controls.",
      highlights: [
        "Compensation Engines",
        "Distributor Portals",
        "Wallet & Payout Workflows",
        "Network Management",
      ],
    },

    whyChoose: {
      title: "Why Invest in Custom MLM Software?",
      description:
        "Custom MLM software helps direct-selling businesses manage distributor networks, compensation calculations, wallets, payouts, reporting, and operational workflows through one centralized platform.",
      items: [
        {
          title: "Automated Commission Calculations",
          description:
            "Calculate suitable commissions, incentives, bonuses, and earnings according to configured compensation rules.",
          icon: Calculator,
        },
        {
          title: "Centralized Network Management",
          description:
            "Manage distributors, sponsors, network structures, statuses, activities, and relevant operational information.",
          icon: Network,
        },
        {
          title: "Transparent Wallet & Payouts",
          description:
            "Maintain structured earning, wallet, transaction, withdrawal, and payout workflows for distributors.",
          icon: Wallet,
        },
        {
          title: "Operational Visibility",
          description:
            "Provide administrators and distributors with relevant dashboards, reports, network information, and earning visibility.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our MLM Software Development Services",
      description:
        "End-to-end MLM software development covering compensation engines, distributor management, network structures, wallets, payouts, dashboards, reporting, and integrations.",
      items: [
        {
          title: "Custom MLM Software Development",
          description:
            "Build MLM platforms around your direct-selling model, distributor structure, compensation rules, products, wallets, and operational requirements.",
          icon: Activity,
          features: [
            "Distributor Management",
            "Network Management",
            "Compensation Rules",
            "Role-Based Access",
            "Admin Controls",
          ],
        },
        {
          title: "MLM Compensation Engine Development",
          description:
            "Develop calculation engines for suitable commission structures, bonuses, incentives, ranks, and compensation workflows.",
          icon: Calculator,
          features: [
            "Commission Calculations",
            "Bonus Rules",
            "Rank Calculations",
            "Incentive Workflows",
            "Earning Statements",
          ],
        },
        {
          title: "Distributor Portal Development",
          description:
            "Create dedicated distributor experiences for network visibility, earnings, wallets, transactions, reports, and account management.",
          icon: LayoutDashboard,
          features: [
            "Distributor Dashboard",
            "Network Visibility",
            "Earning Reports",
            "Wallet Management",
            "Profile Management",
          ],
        },
        {
          title: "Wallet & Payout Development",
          description:
            "Build structured wallet and payout workflows for managing distributor earnings, transactions, withdrawals, and payment records.",
          icon: Wallet,
          features: [
            "Digital Wallet",
            "Earning Credits",
            "Withdrawal Requests",
            "Payout Processing",
            "Transaction History",
          ],
        },
      ],
    },

    features: {
      title: "Key MLM Software Features",
      description:
        "Core functionality for managing distributor networks, compensation calculations, wallets, payouts, reporting, and direct-selling operations.",
      items: [
        {
          title: "Distributor Management",
          description:
            "Manage distributor profiles, sponsors, account statuses, onboarding information, and relevant network activity.",
          icon: Users,
        },
        {
          title: "Network Structure Management",
          description:
            "Represent and manage suitable sponsor, referral, genealogy, or organizational network relationships.",
          icon: Network,
        },
        {
          title: "Commission Engine",
          description:
            "Calculate commissions and incentives according to configured business rules and compensation structures.",
          icon: Calculator,
        },
        {
          title: "Wallet Management",
          description:
            "Track suitable earnings, credits, debits, withdrawals, payouts, balances, and distributor transaction histories.",
          icon: Wallet,
        },
        {
          title: "Rank & Achievement Management",
          description:
            "Track suitable distributor ranks, qualification criteria, achievements, incentives, and progression.",
          icon: Trophy,
        },
        {
          title: "Reports & Analytics",
          description:
            "Provide relevant network, commission, distributor, transaction, payout, and operational reporting.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our MLM Software Development Process",
      description:
        "A structured development process focused on understanding the direct-selling model, mapping compensation rules, designing distributor workflows, and building reliable network operations.",
      items: [
        {
          title: "Business Model Discovery",
          description:
            "Understand your distributor model, network structure, compensation rules, products, ranks, wallets, payouts, users, and administrative requirements.",
        },
        {
          title: "Compensation & System Architecture",
          description:
            "Define network relationships, commission calculations, ranks, wallet workflows, payout processes, permissions, database structures, and integrations.",
        },
        {
          title: "MLM UI/UX Design",
          description:
            "Design distributor dashboards, network views, wallets, earnings screens, reports, administrative controls, and operational workflows.",
        },
        {
          title: "MLM Software Development",
          description:
            "Develop distributor management, network structures, compensation engines, wallets, payouts, dashboards, reporting, APIs, and integrations.",
        },
        {
          title: "Calculation & Workflow Testing",
          description:
            "Validate compensation rules, network relationships, wallet transactions, payouts, permissions, reports, and operational scenarios.",
        },
        {
          title: "Deployment & Support",
          description:
            "Deploy the MLM platform and provide ongoing technical support, rule changes, integrations, optimization, and additional functionality.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom MLM Software Development",
      description:
        "Centralize complex direct-selling operations while improving calculation consistency, distributor visibility, network management, and administrative control.",
      items: [
        {
          title: "Automated Compensation Processing",
          description:
            "Reduce manual calculation work by processing suitable commissions, bonuses, ranks, and incentives through configured rules.",
          icon: Calculator,
        },
        {
          title: "Better Distributor Visibility",
          description:
            "Give distributors access to relevant network information, earnings, wallet activity, ranks, and reports.",
          icon: LayoutDashboard,
        },
        {
          title: "Centralized Network Operations",
          description:
            "Manage distributor relationships, network structures, statuses, transactions, and operational information within one platform.",
          icon: Network,
        },
        {
          title: "Transparent Earnings Records",
          description:
            "Maintain structured records for suitable commissions, bonuses, wallet transactions, withdrawals, and payouts.",
          icon: Wallet,
        },
        {
          title: "Administrative Control",
          description:
            "Give authorized administrators control over distributors, compensation settings, transactions, reports, and operational workflows.",
          icon: Settings2,
        },
        {
          title: "Scalable Network Management",
          description:
            "Support growing distributor networks, transactions, calculations, reporting requirements, and platform functionality.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "MLM Software Development Technologies",
      description:
        "Technologies used to build distributor interfaces, compensation services, relational network data, and background processing workflows.",
      technologies: ["Node.js", "PostgreSQL", "React", "Queues"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We approach MLM software as a rules-driven operational platform, translating network structures, compensation logic, wallets, payouts, and distributor workflows into maintainable software.",
      items: [
        {
          title: "Custom Compensation Logic",
          description:
            "Translate your defined compensation rules, qualification criteria, ranks, and incentive workflows into structured software logic.",
          icon: Calculator,
        },
        {
          title: "Network Architecture",
          description:
            "Design suitable data structures for distributor relationships, sponsorship, genealogy, hierarchy, and network operations.",
          icon: Network,
        },
        {
          title: "Transparent Distributor Experiences",
          description:
            "Provide distributors with relevant dashboards for networks, earnings, wallets, transactions, ranks, and reports.",
          icon: LayoutDashboard,
        },
        {
          title: "Wallet & Payout Workflows",
          description:
            "Build structured transaction, earning, withdrawal, and payout workflows with relevant administrative controls.",
          icon: Wallet,
        },
        {
          title: "Scalable Backend Architecture",
          description:
            "Build backend services capable of supporting increasing distributors, transactions, calculations, and operational workloads.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your MLM platform with compensation changes, troubleshooting, integrations, reporting improvements, and additional development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "MLM Software Solutions for Direct-Selling Businesses",
      description:
        "Custom MLM platforms tailored to different distributor models, product structures, compensation workflows, and network operations.",
      items: [
        {
          title: "Direct Selling",
          description:
            "Manage distributors, product-related activity, compensation, wallets, payouts, ranks, and network operations.",
          icon: Users,
        },
        {
          title: "Referral Networks",
          description:
            "Track suitable referrals, sponsors, network relationships, rewards, qualification rules, and participant activity.",
          icon: Network,
        },
        {
          title: "Product Distribution Networks",
          description:
            "Connect distributor structures with suitable product, sales, commission, incentive, and reporting workflows.",
          icon: Package,
        },
        {
          title: "Wellness & Lifestyle Networks",
          description:
            "Manage distributor accounts, network structures, product-related workflows, earnings, ranks, and payouts.",
          icon: HeartHandshake,
        },
        {
          title: "Beauty & Personal Care Networks",
          description:
            "Support distributor onboarding, network activity, product-related operations, commissions, incentives, and reporting.",
          icon: Sparkles,
        },
        {
          title: "Multi-Region Distributor Networks",
          description:
            "Centralize suitable distributor operations, network structures, reporting, permissions, and payout workflows across multiple regions.",
          icon: Globe,
        },
      ],
    },

    portfolio: {
      title: "MLM Software Development Success Stories",
      description:
        "Direct-selling platforms focused on compensation calculations, distributor management, wallet workflows, network visibility, and operational reporting.",
      items: [
        {
          title: "Distributor Network Platform",
          description:
            "Developed an MLM platform with distributor onboarding, sponsor relationships, network views, commission calculations, wallets, payouts, ranks, and administrative reporting.",
          image: "/project-image.webp",
        },
        {
          title: "Commission & Wallet Management System",
          description:
            "Built a rules-driven compensation engine connected with distributor wallets, earning records, withdrawal workflows, transaction histories, and payout administration.",
          image: "/project-image.webp",
        },
        {
          title: "Direct-Selling Management Portal",
          description:
            "Created distributor and administrator dashboards for network management, earnings visibility, ranks, reports, wallet activity, and operational controls.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire MLM Software Developers",
      description:
        "Partner with our developers to build, integrate, optimize, and support MLM software for distributor networks, compensation calculations, wallets, payouts, and reporting.",
      cta: "Let's Build Your MLM Software",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to translate complex distributor and compensation workflows into structured software platforms.",
      items: [
        {
          name: "Richard Bennett",
          company: "Nexa Distribution",
          location: "United Kingdom",
          review:
            "The biggest challenge for us was keeping commission calculations and distributor records transparent. The new system gives both our administrators and network members much clearer visibility.",
        },
        {
          name: "Kevin Mitchell",
          company: "GrowthLine Network",
          location: "United States",
          review:
            "Skand Tech Hub understood the complexity of our compensation rules and built them into a much more manageable system. Wallets, payouts, network reporting, and distributor dashboards are now connected in one platform.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our MLM Software Development services.",
      items: [
        {
          question: "What is MLM software development?",
          answer:
            "MLM software development involves building a platform for managing distributor networks, compensation calculations, wallets, payouts, ranks, reporting, administrative controls, and other direct-selling workflows.",
        },
        {
          question: "Can you build custom MLM compensation logic?",
          answer:
            "Yes. We can implement suitable compensation rules based on your defined business model, including commission calculations, bonuses, qualification criteria, ranks, and related workflows.",
        },
        {
          question: "Can distributors have their own dashboards?",
          answer:
            "Yes. Distributor portals can provide relevant network information, earnings, wallet balances, transaction histories, ranks, reports, profiles, and other permitted functionality.",
        },
        {
          question: "Can MLM software manage wallets and payouts?",
          answer:
            "Yes. The platform can include suitable wallet balances, earning credits, transaction histories, withdrawal requests, payout processing, and administrative controls.",
        },
        {
          question: "Can the platform handle large distributor networks?",
          answer:
            "The software architecture can be designed according to expected distributor volumes, network structures, calculation workloads, transactions, reporting requirements, and future growth.",
        },
        {
          question: "Do you provide ongoing MLM software support?",
          answer:
            "Yes. We can provide troubleshooting, compensation rule changes, integrations, reporting improvements, performance optimization, and additional MLM platform development.",
        },
      ],
    },
  },
  // Custom Software Development
  {
    title: "Custom Software Development",
    slug: "custom-software-development",
    group: "Software Development",
    icon: BriefcaseBusiness,

    seo: {
      title: "Custom Software Development Services",
      description:
        "Build tailored business software designed around your workflows, users, integrations, and operational requirements with scalable architecture and long-term technical support.",
      keywords: [
        "Custom Software Development",
        "Custom Software Development Services",
        "Business Software Development",
        "Bespoke Software Development",
        "Custom Software Developers",
        "Custom Software Development Company",
      ],
    },

    hero: {
      title: "Custom Software Development Built Around Your Business",
      description:
        "Design and build software that fits your operations instead of forcing your team into generic tools.",
      highlights: [
        "Discovery & Solution Architecture",
        "Role-Based Product Modules",
        "Custom Business Workflows",
        "Long-Term Support Roadmap",
      ],
    },

    whyChoose: {
      title: "Why Invest in Custom Software Development?",
      description:
        "Custom software gives businesses the flexibility to digitize unique workflows, connect existing systems, automate operations, and build functionality that off-the-shelf products cannot adequately support.",
      items: [
        {
          title: "Built Around Your Operations",
          description:
            "Design software around your actual business processes, teams, users, data, and operational requirements.",
          icon: BriefcaseBusiness,
        },
        {
          title: "Workflow Automation",
          description:
            "Automate suitable repetitive tasks, approvals, notifications, calculations, assignments, and operational processes.",
          icon: Workflow,
        },
        {
          title: "Connected Business Systems",
          description:
            "Integrate custom software with suitable APIs, existing applications, databases, cloud services, and external platforms.",
          icon: Boxes,
        },
        {
          title: "Scalable Product Architecture",
          description:
            "Build a technical foundation capable of supporting additional users, modules, workflows, integrations, and business growth.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our Custom Software Development Services",
      description:
        "End-to-end custom software development covering discovery, architecture, business applications, workflow automation, integrations, modernization, and long-term product development.",
      items: [
        {
          title: "Custom Business Software",
          description:
            "Build tailored applications around your operational processes, users, data, business rules, and organizational requirements.",
          icon: BriefcaseBusiness,
          features: [
            "Business Applications",
            "Custom Modules",
            "Role-Based Access",
            "Operational Workflows",
            "Management Dashboards",
          ],
        },
        {
          title: "Workflow Automation Software",
          description:
            "Digitize and automate suitable business processes involving approvals, assignments, statuses, notifications, and recurring activities.",
          icon: Workflow,
          features: [
            "Process Automation",
            "Approval Workflows",
            "Task Automation",
            "Notifications",
            "Business Rules",
          ],
        },
        {
          title: "Software Integration Development",
          description:
            "Connect custom applications with suitable third-party services, existing systems, APIs, databases, and cloud platforms.",
          icon: Boxes,
          features: [
            "REST API Integration",
            "Third-Party Services",
            "Existing System Integration",
            "Cloud APIs",
            "Data Integration",
          ],
        },
        {
          title: "Legacy Software Modernization",
          description:
            "Modernize suitable existing business software with improved architecture, technology, interfaces, integrations, and functionality.",
          icon: RefreshCw,
          features: [
            "Legacy Modernization",
            "Architecture Improvements",
            "Technology Migration",
            "Feature Enhancement",
            "Performance Optimization",
          ],
        },
      ],
    },

    features: {
      title: "Key Custom Software Features",
      description:
        "Flexible capabilities designed according to your users, operational workflows, business rules, data, and integration requirements.",
      items: [
        {
          title: "Role-Based Access",
          description:
            "Provide suitable functionality, permissions, dashboards, and information according to user roles and responsibilities.",
          icon: ShieldCheck,
        },
        {
          title: "Custom Business Workflows",
          description:
            "Translate operational processes into structured digital workflows with relevant rules, stages, and actions.",
          icon: Workflow,
        },
        {
          title: "Management Dashboards",
          description:
            "Provide relevant operational information, metrics, activities, and reports through centralized dashboards.",
          icon: LayoutDashboard,
        },
        {
          title: "API Integrations",
          description:
            "Connect software with suitable third-party platforms, external services, mobile applications, and existing systems.",
          icon: Braces,
        },
        {
          title: "Data Management",
          description:
            "Structure and manage business information, relationships, transactions, records, and operational data.",
          icon: Database,
        },
        {
          title: "Reporting & Analytics",
          description:
            "Generate relevant reports and operational insights around business activities, workflows, users, and data.",
          icon: BarChart3,
        },
      ],
    },

    process: {
      title: "Our Custom Software Development Process",
      description:
        "A structured product development process covering business discovery, solution architecture, UI/UX, engineering, testing, deployment, and long-term support.",
      items: [
        {
          title: "Discovery & Requirement Analysis",
          description:
            "Understand your business processes, users, existing systems, operational challenges, data, integrations, and software objectives.",
        },
        {
          title: "Solution Architecture",
          description:
            "Define software modules, workflows, roles, database structures, APIs, integrations, security requirements, and technical architecture.",
        },
        {
          title: "UI/UX & Product Design",
          description:
            "Design user journeys, dashboards, forms, workflows, interfaces, and role-specific experiences around actual software usage.",
        },
        {
          title: "Software Development",
          description:
            "Develop frontend experiences, backend services, databases, business logic, APIs, integrations, authentication, and required product modules.",
        },
        {
          title: "Testing & Quality Assurance",
          description:
            "Test functionality, workflows, permissions, integrations, data handling, compatibility, security, and application performance.",
        },
        {
          title: "Deployment & Long-Term Support",
          description:
            "Deploy the software and continue supporting it through optimization, integrations, new modules, workflow changes, and future development.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom Software Development",
      description:
        "Build software around your organization instead of adapting critical operations to the limitations of generic off-the-shelf products.",
      items: [
        {
          title: "Software That Fits Your Business",
          description:
            "Create functionality around your actual workflows, users, processes, rules, and operational requirements.",
          icon: BriefcaseBusiness,
        },
        {
          title: "Improved Operational Efficiency",
          description:
            "Digitize and automate suitable manual processes to simplify repetitive work and information movement.",
          icon: Zap,
        },
        {
          title: "Centralized Business Data",
          description:
            "Bring relevant operational information into structured systems instead of relying on fragmented tools and spreadsheets.",
          icon: Database,
        },
        {
          title: "Greater Process Control",
          description:
            "Define workflows, permissions, approvals, statuses, rules, and responsibilities according to organizational requirements.",
          icon: Settings2,
        },
        {
          title: "Flexible Integrations",
          description:
            "Connect custom software with suitable existing systems, APIs, cloud platforms, communication services, and external tools.",
          icon: Boxes,
        },
        {
          title: "Long-Term Scalability",
          description:
            "Extend modules, users, workflows, integrations, infrastructure, and functionality as your business evolves.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Custom Software Development Technologies",
      description:
        "Modern technologies selected according to product architecture, business requirements, integrations, and scalability needs.",
      technologies: ["React", "Node.js", "Python", "Cloud APIs"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We approach custom software development from your operational requirements first, translating business processes into maintainable digital products and scalable technical architecture.",
      items: [
        {
          title: "Discovery-Driven Development",
          description:
            "Understand your workflows, users, operational challenges, and business objectives before defining software functionality.",
          icon: Search,
        },
        {
          title: "Custom Solution Architecture",
          description:
            "Design modules, data structures, integrations, workflows, and technical foundations around your specific requirements.",
          icon: Layers,
        },
        {
          title: "Full-Stack Development",
          description:
            "Build frontend interfaces, backend services, APIs, databases, authentication, integrations, and business functionality.",
          icon: Code2,
        },
        {
          title: "Business System Integration",
          description:
            "Connect custom software with suitable existing applications, third-party services, databases, and cloud platforms.",
          icon: Boxes,
        },
        {
          title: "Scalable Product Engineering",
          description:
            "Build maintainable foundations capable of supporting additional users, functionality, workflows, and integrations.",
          icon: TrendingUp,
        },
        {
          title: "Long-Term Technical Support",
          description:
            "Continue supporting your software with troubleshooting, optimization, integrations, new modules, and future development.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Custom Software Solutions for Different Industries",
      description:
        "Tailored software platforms designed around industry-specific workflows, users, operational processes, and business requirements.",
      items: [
        {
          title: "Enterprise & Corporate",
          description:
            "Build internal platforms, workflow systems, management dashboards, administrative applications, and operational software.",
          icon: Building2,
        },
        {
          title: "Retail & E-Commerce",
          description:
            "Develop custom systems for products, inventory, customers, orders, operations, integrations, and reporting.",
          icon: ShoppingCart,
        },
        {
          title: "Healthcare",
          description:
            "Create appropriate software for administrative processes, service workflows, operational management, communication, and reporting.",
          icon: HeartHandshake,
        },
        {
          title: "Education",
          description:
            "Build platforms for students, administration, courses, operations, communication, workflows, and institutional management.",
          icon: GraduationCap,
        },
        {
          title: "Real Estate",
          description:
            "Develop software for properties, leads, customers, agents, workflows, operations, dashboards, and reporting.",
          icon: Building2,
        },
        {
          title: "Logistics & Operations",
          description:
            "Create operational platforms for workflow management, assignments, tracking, records, dashboards, and business reporting.",
          icon: Truck,
        },
      ],
    },

    portfolio: {
      title: "Custom Software Development Success Stories",
      description:
        "Tailored software platforms focused on business-specific workflows, automation, integrations, centralized operations, and scalable product architecture.",
      items: [
        {
          title: "Business Operations Platform",
          description:
            "Developed a custom platform with role-based modules, workflow automation, centralized business data, dashboards, reporting, and third-party integrations.",
          image: "/project-image.webp",
        },
        {
          title: "Workflow Automation System",
          description:
            "Built software that transformed manual approval, assignment, status, notification, and reporting processes into structured digital workflows.",
          image: "/project-image.webp",
        },
        {
          title: "Legacy Business Software Modernization",
          description:
            "Modernized an existing operational platform with updated architecture, improved interfaces, new integrations, streamlined workflows, and scalable functionality.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Custom Software Developers",
      description:
        "Partner with our software developers to discover, design, build, integrate, modernize, and support tailored digital products around your business operations.",
      cta: "Let's Build Your Custom Software",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to translate unique operational requirements into practical custom software platforms.",
      items: [
        {
          name: "William Carter",
          company: "NorthPeak Operations",
          location: "United Kingdom",
          review:
            "Off-the-shelf tools never matched the way our team actually worked. Skand Tech Hub mapped our processes first and built the software around them, which made adoption much easier across the business.",
        },
        {
          name: "Jason Miller",
          company: "CoreFlow Systems",
          location: "United States",
          review:
            "The platform replaced several disconnected tools and manual workflows. We now have one system for our core operations, reporting, and team processes, with room to keep expanding it.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our Custom Software Development services.",
      items: [
        {
          question: "What is custom software development?",
          answer:
            "Custom software development involves designing and building software around specific business processes, users, workflows, data, integrations, and operational requirements rather than relying entirely on generic off-the-shelf products.",
        },
        {
          question: "When should a business consider custom software?",
          answer:
            "Custom software can be suitable when existing tools do not adequately support important workflows, require excessive manual work, create fragmented data, or cannot provide the functionality and integrations your operations require.",
        },
        {
          question:
            "Can you integrate custom software with our existing systems?",
          answer:
            "Yes. Where suitable APIs or integration methods are available, custom software can connect with existing applications, databases, third-party services, cloud platforms, and other business systems.",
        },
        {
          question: "Can you modernize our existing software?",
          answer:
            "Yes. We can assess suitable existing software and provide architecture improvements, technology migration, UI/UX modernization, integrations, feature development, and performance optimization.",
        },
        {
          question: "Can custom software grow with our business?",
          answer:
            "Yes. The architecture can be planned to support additional modules, users, workflows, integrations, data requirements, and infrastructure as the product and business evolve.",
        },
        {
          question: "Do you provide long-term software support?",
          answer:
            "Yes. We can provide troubleshooting, optimization, integrations, workflow changes, new modules, technology updates, and ongoing custom software development.",
        },
      ],
    },
  },
];
